import { Agent } from "../types/agent.data";
import { useAppSelector } from '../../../redux'
import { AGENT_STORE_NAME, AUTH_STORE_NAME } from "../../../contants/stores";
import { formatDate } from "../../../utils/helpers";
import { search, syncAgentCount } from "../actions/agent.actions";
import Table from "../../../components/Table/Table";
import { useEffect, useState, useCallback } from "react";

export const AgentsList = (props: any) => {
  const loggedInAgent = useAppSelector((state) => state[AUTH_STORE_NAME].loggedInAgent);
  const query = useAppSelector(state => state[AGENT_STORE_NAME].searchQuery);
  const headers = ["Name", "Role", "Active orders", "Last login", "Status", "Action"];
  const [agents, setAgents] = useState(props.agents);

  // Use pagination props from parent
  const currentPage = props.currentPage || 1;
  const setCurrentPage = props.setCurrentPage;
  const rowsPerPage = props.rowsPerPage || 10;
  const setRowsPerPage = props.setRowsPerPage;
  const totalCount = props.totalCount || 0;

  const updateSearchList = useCallback(() => {
    if (!query.length) {
      setAgents(props.agents);
    } else {
      setAgents(search(query));
    }
  }, [query, props.agents]);

  useEffect(() => {
    updateSearchList()
  }, [updateSearchList])
  
  return (
    <>
      {query.length && !agents.length ?
        <div className="w-full order-1 flex-1 bg-white rounded-lg m-4 text-xs">
          No results found
        </div> :
        <>
          <Table headers={headers}>
            {agents && agents.map((agent: Agent) => (
              <tr className="bg-white border-b hover:bg-gray-50" key={agent.id}>
                <th scope="row" className="flex items-center py-4 px-6 text-gray-900 whitespace-nowrap">
                  <div>
                    <div className="text-base font-semibold">{agent.name}</div>
                    <div className="font-normal text-gray-500">{agent.email}</div>
                  </div>
                </th>
                <td className="py-4 px-6">
                  {agent.isSuperAdmin ? 'Tech admin' : agent.isAdmin ? 'OM admin' : 'Agent'}
                </td>
                <td className="flex items-center gap-2 py-4 px-6">
                  <div onClick={() => syncAgentCount(agent.id)}
                    className="inline-flex justify-center rounded-md cursor-pointer bg-white p-2 text-sm font-medium text-gray-900 hover:text-opacity-50 hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75">
                    <svg
                      className="h-4 w-4"
                      fill="#000000"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      {...props}
                    >
                      <path d="M19.91,15.51H15.38a1,1,0,0,0,0,2h2.4A8,8,0,0,1,4,12a1,1,0,0,0-2,0,10,10,0,0,0,16.88,7.23V21a1,1,0,0,0,2,0V16.5A1,1,0,0,0,19.91,15.51ZM12,2A10,10,0,0,0,5.12,4.77V3a1,1,0,0,0-2,0V7.5a1,1,0,0,0,1,1h4.5a1,1,0,0,0,0-2H6.22A8,8,0,0,1,20,12a1,1,0,0,0,2,0A10,10,0,0,0,12,2Z" />
                    </svg>
                  </div>

                  {(agent.activeGroupOrders ?? 0) + (agent.activeOrders ?? 0)}
                </td>
                <td className="py-4 px-6">
                  {formatDate(agent?.lastLogin)}
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center">
                    <div className={`h-2.5 w-2.5 rounded-full bg-${agent.status === "ONLINE" ? "green-400" : agent.status === "OFFLINE" ? "red-500" : "yellow-400"} mr-2`}></div> {agent.status}
                  </div>
                </td>
                <td className="flex gap-2 py-4 px-6">
                  <button
                    onClick={() => props.onPressItem(agent, 'EDIT')}
                    data-modal-toggle="editUserModal" className="font-medium text-blue-600 hover:underline">Edit</button>
                  {loggedInAgent.isSuperAdmin && <button
                    onClick={() => props.onPressItem(agent, 'DELETE')}
                    data-modal-toggle="editUserModal" className="font-medium text-red-600 hover:underline">Delete</button>}
                </td>
              </tr>
            ))}
          </Table>
          {/* Pagination Controls - only show when not searching */}
          {query.trim().length === 0 && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-4 gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Rows per page:</span>
                <select
                  className="border border-gray-300 rounded px-2 py-1 text-xs"
                  value={rowsPerPage}
                  onChange={e => { 
                    if (setRowsPerPage) setRowsPerPage(Number(e.target.value)); 
                    if (setCurrentPage) setCurrentPage(1); 
                  }}
                >
                  {[5, 10, 20, 50, 100].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                {/* Jump -10 pages */}
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs"
                  onClick={() => setCurrentPage && setCurrentPage(Math.max(1, currentPage - 10))}
                  disabled={currentPage <= 10}
                >
                  &#171;
                </button>
                {/* Previous page */}
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs"
                  onClick={() => setCurrentPage && setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {/* Current page */}
                <button
                  className="px-2 py-1 rounded text-xs bg-blue-600 text-white cursor-default"
                  disabled
                >
                  {currentPage}
                </button>
                {/* Next page (show only if not last page) */}
                {currentPage < Math.ceil(totalCount / rowsPerPage) && (
                  <button
                    className="px-2 py-1 rounded text-xs bg-gray-200 text-gray-700 hover:bg-gray-300"
                    onClick={() => setCurrentPage && setCurrentPage(currentPage + 1)}
                  >
                    {currentPage + 1}
                  </button>
                )}
                {/* Next page button */}
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs"
                  onClick={() => setCurrentPage && setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= Math.ceil(totalCount / rowsPerPage)}
                >
                  Next
                </button>
                {/* Jump +10 pages */}
                <button
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 text-xs"
                  onClick={() => setCurrentPage && setCurrentPage(Math.min(Math.ceil(totalCount / rowsPerPage), currentPage + 10))}
                  disabled={currentPage >= Math.ceil(totalCount / rowsPerPage)}
                >
                  &#187;
                </button>
              </div>
              <div className="text-xs text-gray-600">
                {query.trim().length > 0 ? (
                  `Found ${agents.length} search results`
                ) : (
                  `Showing ${agents.length} of ${totalCount} (Page ${currentPage})`
                )}
              </div>
            </div>
          )}
          {/* Show search results info when searching */}
          {query.trim().length > 0 && (
            <div className="text-xs text-gray-600 mt-4 text-center">
              Found {agents.length} search results for "{query}"
            </div>
          )}
        </>
      }
    </>
  );
}